<!--
  The visitor and member side of the ticket: one call to action that reflects
  the event's state (open, waitlist, full, external, concluded), the inline
  registration form, and the member's own registration once they have one.
-->
<script>
  import { getContext } from 'svelte';
  import { page } from '$app/state';
  import t from '#lib/i18n.svelte.js';
  import { determine_link_type } from '#lib/util.js';
  import EventRegistrationForm from '#lib/EventRegistrationForm.svelte';
  import MyRegistrationCard from '#lib/MyRegistrationCard.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import EventChip from '#lib/EventChip.svelte';
  import '#lib/system-styles/event-ui.css';

  let {
    entry,
    additional_entry_data,
    capacity,
    is_restricted = false,
    has_concluded = false,
    /** Open the form right away (e-mail links carry ?show_registration_form=true) */
    open_form = false,
    /** Back from Checkout: the seats are being confirmed (see MyRegistrationCard) */
    confirming_payment = false,
    onchanged,
    /** Refetches registrations and seat counts (after a partial failure) */
    onrefresh,
  } = $props();

  const site_settings = getContext('site_settings');

  let is_logged_in = $derived(Boolean(page.data.current_user));
  // svelte-ignore state_referenced_locally
  let form_open = $state(open_form);
  // The arrival copy belongs to that first, unrequested opening only: the CTA,
  // the one way to open the form by hand, clears it
  // svelte-ignore state_referenced_locally
  let returning = $state(open_form);

  // Only the viewer's own rows: the server hands admins every registration, so
  // without this an admin viewing the CTA (e.g. in external mode) would see
  // other people's bookings as their own.
  let my_registrations = $derived(
    (additional_entry_data?.event_registrations ?? []).filter(reg => reg.user_id === page.data.current_user?.user_id)
  );
  let own_registrations = $derived(my_registrations.filter(reg => reg.status !== 'refunded'));
  let has_refunded = $derived(my_registrations.some(reg => reg.status === 'refunded'));

  let login_href = $derived(
    `/login?return_to=${encodeURIComponent(`${entry.slug}-${entry.entry_id}?show_registration_form=true`)}`
  );

  // determine_link_type gives a bare 'www.example.com' its scheme (otherwise it
  // resolves as a relative path) and turns a plain address into a mailto: link.
  let external_link = $derived(determine_link_type(entry.link_href?.trim() || ''));
  let external_href = $derived(external_link?.value || '');
  let is_mailto = $derived(external_link?.type === 'email');

  let cta_label = $derived(
    entry.link_button_label || (capacity.has_waitlist_space ? t('add_to_waitlist') : t('register_now'))
  );

  // The registration is a section of the ticket, so it only exists when it
  // has something to say: the member's own registration, or a way to register.
  // Registration off, cancelled, postponed or concluded: the ticket ends with
  // its facts (the cancelled and postponed notices are the ticket's own)
  let is_open = $derived(
    (entry.registration_mode === 'internal' || entry.registration_mode === 'external')
    && !is_restricted && !has_concluded
  );
  let has_content = $derived(
    own_registrations.length > 0
    || (is_open && (entry.registration_mode === 'internal' || external_href || entry.link_button_label))
  );

  function handle_changed(counts) {
    form_open = false;
    onchanged?.(counts);
  }
</script>

{#if has_content}
  <div class='event-ticket-section event-lines'>
    {#if own_registrations.length > 0}
      <MyRegistrationCard
        {entry}
        registrations={own_registrations}
        {capacity}
        {is_restricted}
        {has_concluded}
        {confirming_payment}
        onchanged={handle_changed}
        {onrefresh}
      />

    {:else if entry.registration_mode === 'external'}
      {#if external_href}
        <a
          href={external_href}
          class='full-width'
          target={is_mailto ? undefined : '_blank'}
          rel={is_mailto ? undefined : 'noopener noreferrer'}
        >
          <ButtonLabel
            variant={`brand ${site_settings.primary_control_surface}`}
            end_icon={is_mailto ? 'email' : 'external-link'}
            class='full-width'
          >
            {entry.link_button_label || t('register_now')}
          </ButtonLabel>
        </a>
        {#if is_mailto}
          <p class='text-control text-2 leading-trim'>{entry.link_href.trim()}</p>
        {/if}
      {:else}
        <p class='text-control leading-trim'>{entry.link_button_label}</p>
      {/if}

    {:else if capacity.is_full && !capacity.has_waitlist_space}
      <div class='event-compact event-facts'>
        <EventChip tone='crit'>{t('sold_out')}</EventChip>
      </div>
      <p class='text-control text-2 leading-trim'>
        {t('all_seats_taken', { total: capacity.max_attendees })}
        {#if capacity.waitlist_capacity > 0}
          {t('waitlist_full')}
        {/if}
      </p>

    {:else if !is_logged_in}
      <a href={login_href} class='full-width'>
        <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`} class='full-width'>
          {cta_label}
        </ButtonLabel>
      </a>

    {:else if form_open}
      <EventRegistrationForm
        {entry}
        {capacity}
        {returning}
        onsubmitted={handle_changed}
        oncancel={() => { form_open = false; }}
      />

    {:else}
      {#if has_refunded}
        <p class='text-control text-2 leading-trim'>{t('refunded_note')}</p>
      {/if}
      <button type='button' class='full-width' onclick={() => { form_open = true; returning = false; }}>
        <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`} class='full-width'>
          {cta_label}
        </ButtonLabel>
      </button>
    {/if}
  </div>
{/if}
